import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { buildApiUrl } from '../config/api';
import {
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  getRefreshToken,
  setRefreshToken,
  onUnauthorized,
} from '../utils/apiAuth';

/**
 * État d'authentification global.
 *
 * status :
 * - 'loading'   : bootstrap en cours (on interroge /api/auth/config puis /me) ;
 * - 'anonymous' : auth requise mais pas (ou plus) connecté → afficher le login ;
 * - 'authenticated' : connecté (ou auth désactivée côté backend).
 *
 * Principe du gate conditionnel : si le backend n'exige pas d'auth
 * (auth_required=false) ou si /api/auth/config est injoignable, on NE bloque PAS
 * l'app (le vrai garde-fou reste le middleware backend). Le gate frontend n'est
 * qu'une aide UX.
 */
const AuthContext = createContext(null);

async function fetchJson(endpoint, options) {
  if (typeof fetch !== 'function') throw new Error('fetch indisponible');
  const res = await fetch(buildApiUrl(endpoint), options);
  return res;
}

/**
 * Mot de passe oublié (mode Supabase) : demande l'envoi du lien.
 *
 * Le serveur répond la même chose que l'adresse existe ou non ; on ne
 * distingue donc que la limitation de débit (429) et l'absence de réseau.
 * Retourne 'envoye' | 'trop-de-tentatives' ; lève si le serveur est injoignable.
 */
export async function demanderReinitialisation(email) {
  let res;
  try {
    res = await fetchJson('auth/recover', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch (_) {
    throw new Error('Serveur injoignable');
  }
  return res.status === 429 ? 'trop-de-tentatives' : 'envoye';
}

/**
 * Fixe le nouveau mot de passe avec le jeton de la session de récupération.
 *
 * Appel `fetch` direct, volontairement hors des clients API : ceux-ci
 * joindraient le credential stocké et déclencheraient la déconnexion globale
 * sur un 401, alors qu'ici un 401 signifie seulement « lien expiré ».
 */
export async function definirNouveauMotDePasse(jetonRecuperation, nouveauMotDePasse) {
  let res;
  try {
    res = await fetchJson('auth/password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jetonRecuperation}`,
      },
      body: JSON.stringify({ new_password: nouveauMotDePasse }),
    });
  } catch (_) {
    throw new Error('Serveur injoignable');
  }
  if (res.ok) return true;
  if (res.status === 401) {
    throw new Error('Ce lien a expiré ou a déjà été utilisé. Demandez-en un nouveau.');
  }
  if (res.status === 429) {
    throw new Error('Trop de tentatives. Réessayez dans une minute.');
  }
  let msg = 'Mot de passe refusé.';
  try {
    const body = await res.json();
    if (body && typeof body.detail === 'string') msg = `Mot de passe refusé : ${body.detail}`;
  } catch (_) {}
  throw new Error(msg);
}

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [authRequired, setAuthRequired] = useState(false);
  const [mode, setMode] = useState('none');
  // 'local' par défaut : hypothèse la plus sûre tant que /auth/config n'a pas répondu.
  const [dataLocality, setDataLocality] = useState('local');
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  // Miroir de authRequired pour les callbacks à deps stables (handler 401).
  const authRequiredRef = useRef(false);
  useEffect(() => {
    authRequiredRef.current = authRequired;
  }, [authRequired]);

  const loadMe = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return null;
    try {
      const res = await fetchJson('auth/me', {
        headers: { Authorization: `Bearer ${token}`, 'X-API-Key': token },
      });
      if (res.ok) return await res.json();
    } catch (_) {
      /* réseau indisponible : traité comme non authentifié */
    }
    return null;
  }, []);

  /**
   * Valide le credential stocké contre une route protégée.
   * Retourne l'identité (`{}` si valide sans utilisateur, cas clé API), ou
   * `null` si le credential est absent, refusé ou le serveur injoignable.
   */
  const validerCredential = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return null;
    try {
      const res = await fetchJson('auth/validate', {
        headers: { Authorization: `Bearer ${token}`, 'X-API-Key': token },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.user || {};
    } catch (_) {
      return null;
    }
  }, []);

  const bootstrap = useCallback(async () => {
    setStatus('loading');
    let cfg = { auth_required: false, mode: 'none', data_locality: 'local' };
    try {
      const res = await fetchJson('auth/config');
      if (res.ok) cfg = await res.json();
    } catch (_) {
      // /config injoignable (backend down, tests) : on n'impose pas de gate.
      cfg = { auth_required: false, mode: 'none', data_locality: 'local' };
    }
    setAuthRequired(!!cfg.auth_required);
    setMode(cfg.mode || 'none');
    setDataLocality(cfg.data_locality || 'local');

    if (!cfg.auth_required) {
      setStatus('authenticated');
      return;
    }

    // Au rechargement, la validité du credential stocké se juge sur une route
    // réellement protégée. `auth/me` ne convient pas seul : en mode clé API il
    // échoue (pas d'utilisateur JWT) alors que la clé est parfaitement valide,
    // ce qui renvoyait l'utilisateur au login à chaque rafraîchissement.
    const identite = await validerCredential();
    if (identite) {
      setUser(identite.user_id ? identite : await loadMe());
      setStatus('authenticated');
    } else {
      clearAuthToken();
      setUser(null);
      setStatus('anonymous');
    }
  }, [loadMe, validerCredential]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // 401 global émis par les clients API → déconnexion + retour au login.
  useEffect(() => {
    return onUnauthorized(() => {
      clearAuthToken();
      setUser(null);
      // On ne repasse en 'anonymous' que si l'auth est réellement requise.
      if (authRequiredRef.current) setStatus('anonymous');
    });
  }, []);

  // --- Renouvellement automatique de la session (mode Supabase) ---
  //
  // Un jeton d'acces Supabase vit UNE HEURE. Sans ce mecanisme, toute
  // l'application tombait en 401 d'un bloc au bout de ce delai, jusqu'a une
  // reconnexion manuelle — constate en production le 2026-09-10.
  const minuteurRef = useRef(null);
  // `renouveler` doit reprogrammer le prochain cycle, et le programmateur doit
  // appeler `renouveler` : une reference casse ce cycle sans fermeture perimee.
  const renouvelerRef = useRef(null);

  const annulerRenouvellement = useCallback(() => {
    if (minuteurRef.current) {
      clearTimeout(minuteurRef.current);
      minuteurRef.current = null;
    }
  }, []);

  const programmerRenouvellement = useCallback(
    (dureeSecondes) => {
      annulerRenouvellement();
      const duree = Number(dureeSecondes) || 3600;
      // On renouvelle a 80 % de la duree de vie, avec un plancher d'une minute :
      // assez tot pour absorber une horloge decalee ou un reseau lent, assez
      // tard pour ne pas marteler Supabase.
      const delai = Math.max(60, Math.floor(duree * 0.8)) * 1000;
      minuteurRef.current = setTimeout(() => {
        if (renouvelerRef.current) renouvelerRef.current();
      }, delai);
    },
    [annulerRenouvellement]
  );

  const renouveler = useCallback(async () => {
    const jeton = getRefreshToken();
    if (!jeton) return false;

    let res;
    try {
      res = await fetchJson('auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: jeton }),
      });
    } catch (_) {
      // Serveur injoignable : on NE deconnecte PAS. On retentera dans une
      // minute ; deconnecter sur une panne reseau serait le pire des choix.
      programmerRenouvellement(60);
      return false;
    }

    if (!res.ok) {
      // 503 : panne de Supabase — on retente sans deconnecter.
      if (res.status === 503) {
        programmerRenouvellement(60);
        return false;
      }
      // 401 : le jeton de renouvellement est mort, reconnexion necessaire.
      annulerRenouvellement();
      clearAuthToken();
      setUser(null);
      if (authRequiredRef.current) setStatus('anonymous');
      return false;
    }

    const data = await res.json();
    setAuthToken(data.access_token);
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    programmerRenouvellement(data.expires_in);
    return true;
  }, [annulerRenouvellement, programmerRenouvellement]);

  useEffect(() => {
    renouvelerRef.current = renouveler;
  }, [renouveler]);

  // Au chargement de la page : une session peut exister en localStorage avec un
  // jeton d'acces deja expire. On programme donc le cycle des le depart.
  useEffect(() => {
    if (getRefreshToken()) programmerRenouvellement(60);
    return annulerRenouvellement;
  }, [programmerRenouvellement, annulerRenouvellement]);

  /** Connexion par identifiants (mode JWT). */
  const login = useCallback(async (username, password) => {
    setError(null);
    let res;
    try {
      res = await fetchJson('auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
    } catch (e) {
      const msg = 'Serveur injoignable';
      setError(msg);
      throw new Error(msg);
    }
    if (!res.ok) {
      let msg = 'Identifiants invalides';
      try {
        const body = await res.json();
        if (body && body.detail) msg = typeof body.detail === 'string' ? body.detail : msg;
      } catch (_) {}
      setError(msg);
      throw new Error(msg);
    }
    const data = await res.json();
    setAuthToken(data.access_token);
    // Mode Supabase : la session doit etre renouvelee avant expiration.
    // Absents en mode maison, ou le jeton vit 24 h et n'est pas renouvelable.
    if (data.refresh_token) {
      setRefreshToken(data.refresh_token);
      programmerRenouvellement(data.expires_in);
    }
    setUser(data.user || (await loadMe()));
    setStatus('authenticated');
    return data.user;
  }, [loadMe, programmerRenouvellement]);

  /**
   * Connexion par clé API (mode apikey).
   *
   * La sonde de validation était `/api/health`, route explicitement PUBLIQUE :
   * elle répondait 200 pour n'importe quelle chaîne, l'application passait en
   * `authenticated` et n'échouait qu'au premier appel utile. On interroge
   * désormais `/api/auth/validate`, réellement protégée, et l'état ne devient
   * `authenticated` qu'après un vrai HTTP 200.
   */
  const loginWithApiKey = useCallback(async (apiKey) => {
    setError(null);

    const echouer = (msg) => {
      clearAuthToken();
      setUser(null);
      setStatus('anonymous');
      setError(msg);
      throw new Error(msg);
    };

    let probe;
    try {
      probe = await fetchJson('auth/validate', {
        headers: { Authorization: `Bearer ${apiKey}`, 'X-API-Key': apiKey },
      });
    } catch (_) {
      // Réseau indisponible : on ne stocke RIEN. Conserver une clé non validée
      // reproduirait exactement le défaut corrigé ici.
      echouer('Serveur injoignable');
    }

    if (!probe.ok) {
      echouer(probe.status === 401 ? 'Clé API invalide' : `Validation refusée (HTTP ${probe.status})`);
    }

    // Le credential n'est stocké qu'une fois prouvé valide.
    setAuthToken(apiKey);

    let identite = null;
    try {
      identite = (await probe.json()).user || null;
    } catch (_) {
      /* corps non-JSON : le 200 suffit à valider le credential */
    }
    // En mode JWT, /auth/me reste plus complet (email, dates) que la session.
    setUser(identite || (await loadMe()));
    setStatus('authenticated');
  }, [loadMe]);

  const logout = useCallback(() => {
    clearAuthToken();
    setUser(null);
    if (authRequired) setStatus('anonymous');
    else setStatus('authenticated');
  }, [authRequired]);

  const value = {
    status,
    authRequired,
    mode,
    dataLocality,
    user,
    error,
    isAuthenticated: status === 'authenticated',
    login,
    loginWithApiKey,
    logout,
    reload: bootstrap,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>');
  return ctx;
}

export default AuthContext;
